import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { DEFAULT_TRIAGE_TIER, isNeedsTriage, viewerQualifies } from '../components/TicketUi';

const REQUIRED_TIER_OPTIONS = [
  { value: 0, label: '0 - No Access' },
  { value: 2, label: '2 - Routine Maintenance' },
  { value: 3, label: '3 - Repair Tech' },
  { value: 4, label: '4 - Senior Tech' }
];

// Owns the data-fetching, mutation, and derived-permission logic for
// MachineIssueDetail.js. Relocated verbatim from the page component - see
// MachineIssueDetail.test.js, especially the Promote to Known Issue coverage.
function useMachineIssueDetail(issueId) {
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const techTier = currentUser?.techTier ?? currentUser?.tech_tier ?? 0;

  const [issue, setIssue] = useState(null);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showTriage, setShowTriage] = useState(false);
  const [triageForm, setTriageForm] = useState({ impact: 'observation', requiredTechTier: DEFAULT_TRIAGE_TIER, note: '' });
  const [triageError, setTriageError] = useState('');

  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateForm, setEscalateForm] = useState({ requiredTechTier: 3, note: '' });
  const [escalateError, setEscalateError] = useState('');

  const [showReassign, setShowReassign] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignError, setReassignError] = useState('');

  const [showResolve, setShowResolve] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveError, setResolveError] = useState('');

  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showPromote, setShowPromote] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ description: '', guidanceText: '' });
  const [promoteError, setPromoteError] = useState('');
  const [promoting, setPromoting] = useState(false);

  const loadIssue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/machines/issues/${issueId}`);
      const issueData = response.data;
      setIssue(issueData);
      setNotes(issueData.notes || []);
      setUpdates(issueData.updates || []);
      setError('');
    } catch (err) {
      setError('Failed to load issue details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [issueId]);

  const loadUsers = useCallback(async () => {
    // Viewing a ticket is universal (Tier 0+), but this list is only ever used
    // by the reassign picker (Tier 4+/admin) and the endpoint itself is
    // Tier 3+ - skip the call below that floor so a lower-tier viewer doesn't
    // get hit with a spurious "no permission" toast just for opening a ticket.
    if (!isAdmin && techTier < 3) return;
    try {
      // Server already filters to tech_tier >= 3 (or admin) - no client-side
      // re-filter needed, the server is the source of truth for eligibility.
      const response = await apiService.getMachineIssueAssignableUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [isAdmin, techTier]);

  useEffect(() => {
    loadIssue();
    loadUsers();
  }, [loadIssue, loadUsers]);

  const timelineEntries = useMemo(() => {
    const updateEntries = updates.map((entry) => ({ ...entry, type: 'update' }));
    const noteEntries = notes.map((entry) => ({ ...entry, type: 'note' }));

    return [...updateEntries, ...noteEntries]
      .filter((entry) => entry.created_at)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [notes, updates]);

  const assigneeOptions = useMemo(() => {
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email || 'Unknown User',
      searchText: `${user.first_name || ''} ${user.last_name || ''} ${user.username || ''} ${user.email || ''}`.toLowerCase()
    }));
  }, [users]);

  const selectedReassignOption = useMemo(() => {
    return assigneeOptions.find((option) => String(option.value) === String(reassignTo)) || null;
  }, [assigneeOptions, reassignTo]);

  const assigneeSelectStyles = useMemo(() => ({
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menu: (base) => ({ ...base, zIndex: 2000 }),
    control: (base) => ({
      ...base,
      background: '#13151f',
      borderColor: '#2d3148',
      minHeight: 44
    }),
    singleValue: (base) => ({ ...base, color: '#e8eaf2' }),
    input: (base) => ({ ...base, color: '#e8eaf2' }),
    placeholder: (base) => ({ ...base, color: '#4a5070' }),
    menuList: (base) => ({ ...base, background: '#13151f' }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused ? '#212538' : '#13151f',
      color: '#e8eaf2'
    })
  }), []);

  // Derived permission flags - the server is always the final source of truth on
  // every action below, these just control which buttons are shown.
  const needsTriage = issue ? isNeedsTriage(issue) : false;
  const qualifies = issue ? viewerQualifies(issue, techTier, isAdmin) : false;
  // Triage is Tier 3+/Admin, on any ticket regardless of whether it's already
  // been triaged (docs/MACHINE_TICKET_TIERING_REQUIREMENTS.md §8) - Needs
  // Triage exists to catch a reporter's misjudgment, not to gate a tech who's
  // already qualified and looking at the ticket. Floor is Tier 3, not Tier 2:
  // Tier 2 ("Routine Maintenance") is hands-on upkeep and logging new
  // tickets, not working the reported-ticket queue at all (2026-08-06
  // correction, see requirements §18 item 15). Add Note stays Tier 2+ - it's
  // communication, not a workflow action.
  const canTriage = issue && issue.status === 'open' && (isAdmin || techTier >= 3);
  const canClaim = issue && issue.status === 'open' && issue.required_tech_tier !== null &&
    issue.assigned_to !== currentUser?.id &&
    (isAdmin || (techTier >= 3 && issue.required_tech_tier <= techTier));
  const canEscalate = issue && issue.status === 'open' && issue.assigned_to === currentUser?.id &&
    (issue.required_tech_tier ?? 0) < 4;
  const canResolve = issue && issue.status === 'open' && (isAdmin || issue.assigned_to === currentUser?.id);
  const canReopen = issue && issue.status === 'resolved' && (isAdmin || issue.resolved_by === currentUser?.id);
  const canReassign = issue && issue.status === 'open' && (isAdmin || techTier >= 4);
  const canAddNote = issue && issue.status === 'open' && (isAdmin || techTier >= 2);
  // Promotion is available regardless of ticket status - a chronic pattern is
  // usually only obvious after resolving the same symptom more than once
  // (docs/MACHINE_TICKET_TIERING_REQUIREMENTS.md §12). Gate matches plain
  // Known Issue creation (Tier 4+/Admin). Promoting an open ticket also closes
  // it (see submitPromote), so on an open ticket the promoter additionally
  // needs resolve permission - same "I did the work, I'm vouching it's fixed"
  // bar §9 sets for any resolution, not a separate rule for promotion.
  const canPromoteToKnownIssue = issue && (isAdmin || techTier >= 4) &&
    (issue.status !== 'open' || isAdmin || issue.assigned_to === currentUser?.id);

  const openTriage = () => {
    if (!issue) return;
    setTriageError('');
    setShowEscalate(false);
    setShowReassign(false);
    setShowResolve(false);
    setTriageForm({
      impact: issue.impact || 'observation',
      requiredTechTier: issue.required_tech_tier ?? DEFAULT_TRIAGE_TIER,
      note: ''
    });
    setShowTriage(true);
  };

  const submitTriage = async () => {
    try {
      setSaving(true);
      setTriageError('');
      await apiService.triageIssue(issueId, {
        impact: triageForm.impact,
        requiredTechTier: parseInt(triageForm.requiredTechTier, 10),
        note: triageForm.note || undefined
      });
      setShowTriage(false);
      await loadIssue();
    } catch (err) {
      setTriageError(err.response?.data?.error || 'Failed to triage issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClaim = async () => {
    try {
      setSaving(true);
      setError('');
      await apiService.claimIssue(issueId);
      await loadIssue();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to claim issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openEscalate = () => {
    if (!issue) return;
    setEscalateError('');
    setShowTriage(false);
    setShowReassign(false);
    setShowResolve(false);
    const currentTier = issue.required_tech_tier ?? 0;
    const nextOption = REQUIRED_TIER_OPTIONS.find((option) => option.value > currentTier);
    setEscalateForm({ requiredTechTier: nextOption ? nextOption.value : 4, note: '' });
    setShowEscalate(true);
  };

  const submitEscalate = async () => {
    if (!escalateForm.note.trim()) {
      setEscalateError('An explanatory note is required to escalate.');
      return;
    }
    try {
      setSaving(true);
      setEscalateError('');
      await apiService.escalateIssue(issueId, {
        requiredTechTier: parseInt(escalateForm.requiredTechTier, 10),
        note: escalateForm.note.trim()
      });
      setShowEscalate(false);
      await loadIssue();
    } catch (err) {
      setEscalateError(err.response?.data?.error || 'Failed to escalate issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openResolve = () => {
    if (!issue) return;
    setResolveError('');
    setShowTriage(false);
    setShowEscalate(false);
    setShowReassign(false);
    setResolveNote('');
    setShowResolve(true);
  };

  const submitResolve = async () => {
    if (!resolveNote.trim()) {
      setResolveError('An explanatory note is required to resolve.');
      return;
    }
    try {
      setSaving(true);
      setResolveError('');
      await apiService.resolveIssue(issueId, { note: resolveNote.trim() });
      setShowResolve(false);
      await loadIssue();
    } catch (err) {
      setResolveError(err.response?.data?.error || 'Failed to resolve issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    try {
      setSaving(true);
      setError('');
      await apiService.reopenIssue(issueId, {});
      await loadIssue();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openReassign = () => {
    if (!issue) return;
    setReassignError('');
    setShowTriage(false);
    setShowEscalate(false);
    setShowResolve(false);
    setReassignTo(issue.assigned_to || '');
    setShowReassign(true);
  };

  const submitReassign = async () => {
    if (!reassignTo) {
      setReassignError('Select a member to assign this ticket to.');
      return;
    }
    try {
      setSaving(true);
      setReassignError('');
      await apiService.reassignIssue(issueId, parseInt(reassignTo, 10));
      setShowReassign(false);
      await loadIssue();
    } catch (err) {
      setReassignError(err.response?.data?.error || 'Failed to reassign issue');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) {
      setNoteError('Note cannot be empty.');
      return;
    }
    try {
      setSaving(true);
      setNoteError('');
      await apiService.post(`/machines/issues/${issueId}/notes`, { note: noteText.trim() });
      setNoteText('');
      await loadIssue();
    } catch (err) {
      setNoteError('Failed to add note');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openPromote = () => {
    if (!issue) return;
    setPromoteError('');
    setPromoteForm({ description: issue.description || '', guidanceText: '' });
    setShowPromote(true);
  };

  const submitPromote = async (e) => {
    e.preventDefault();
    if (!promoteForm.description.trim()) {
      setPromoteError('Description is required.');
      return;
    }
    try {
      setPromoting(true);
      setPromoteError('');
      await apiService.createKnownIssue({
        machineId: issue.machine_id,
        description: promoteForm.description.trim(),
        guidanceText: promoteForm.guidanceText.trim() || undefined,
        sourceIssueId: issue.id
      });
      // A promoted ticket is done - the symptom is now tracked as a standing
      // Known Issue rather than a workflow item, so close the ticket rather
      // than leaving it open and duplicating what the Known Issue now covers.
      if (issue.status === 'open') {
        await apiService.resolveIssue(issueId, { note: 'Promoted to Known Issue.' });
      }
      setShowPromote(false);
      await loadIssue();
    } catch (err) {
      setPromoteError(err.response?.data?.error || 'Failed to promote to Known Issue');
      console.error(err);
    } finally {
      setPromoting(false);
    }
  };

  const handleDelete = async (onDeleted) => {
    try {
      setDeleting(true);
      await apiService.delete(`/machines/issues/${issueId}`);
      if (onDeleted) onDeleted();
    } catch (err) {
      setError('Failed to delete issue');
      console.error(err);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return {
    isAdmin,
    techTier,
    currentUser,
    issue,
    users,
    notes,
    updates,
    saving,
    loading,
    error,
    setError,
    showTriage,
    setShowTriage,
    triageForm,
    setTriageForm,
    triageError,
    showEscalate,
    setShowEscalate,
    escalateForm,
    setEscalateForm,
    escalateError,
    showReassign,
    setShowReassign,
    reassignTo,
    setReassignTo,
    reassignError,
    showResolve,
    setShowResolve,
    resolveNote,
    setResolveNote,
    resolveError,
    noteText,
    setNoteText,
    noteError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleting,
    showPromote,
    setShowPromote,
    promoteForm,
    setPromoteForm,
    promoteError,
    promoting,
    timelineEntries,
    assigneeOptions,
    selectedReassignOption,
    assigneeSelectStyles,
    needsTriage,
    qualifies,
    canTriage,
    canClaim,
    canEscalate,
    canResolve,
    canReopen,
    canReassign,
    canAddNote,
    canPromoteToKnownIssue,
    openTriage,
    submitTriage,
    handleClaim,
    openEscalate,
    submitEscalate,
    openResolve,
    submitResolve,
    handleReopen,
    openReassign,
    submitReassign,
    submitNote,
    openPromote,
    submitPromote,
    handleDelete
  };
}

export default useMachineIssueDetail;
